import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { RequestItemsClient, RequestItemLogDto } from '../../web-api-client';
import { API_CONFIG } from '../../config/api';
import { createAuthenticatedFetch } from '../../utils/apiClient';

interface ActivityLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestItemId: number;
  taskName: string;
}

export default function ActivityLogModal({
  isOpen,
  onClose,
  requestItemId,
  taskName
}: ActivityLogModalProps) {
  const [logs, setLogs] = useState<RequestItemLogDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
    }
  }, [isOpen, requestItemId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
      const data = await client.getRequestItemLogs(requestItemId);
      setLogs(data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ClockIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Activity Log</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">Task: {taskName}</p>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading activity...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No activity recorded yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className="border-l-4 border-blue-500 bg-gray-50 p-4 rounded-r-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{log.action}</p>
                      <p className="text-sm text-gray-600">
                        by {log.performedByEmail || log.performedBy}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {log.created ? new Date(log.created + 'Z').toLocaleString() : 'N/A'}
                    </span>
                  </div>

                  {log.oldValues && log.newValues && (
                    <div className="text-sm bg-white p-2 rounded border border-gray-200 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">{log.oldValues}</span>
                        <span className="text-gray-400">→</span>
                        <span className="text-green-600 font-medium">{log.newValues}</span>
                      </div>
                    </div>
                  )}

                  {log.remarks && (
                    <p className="text-sm text-gray-700 mt-2 italic">
                      "{log.remarks}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
